import { Link } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { useTranslation } from "../i18n/LanguageContext";
import emailIcon from "../assets/svg/ci--mail.svg";
import facebookIcon from "../assets/svg/ic--baseline-facebook.svg";
import instagramIcon from "../assets/svg/mdi--instagram.svg";
import linkedinIcon from "../assets/svg/ri--linkedin-fill.svg";
import logo from "../assets/logo.svg";
import MatrixBackdrop from "./HomePage/MatrixBackdrop";

const CONTACT_EMAIL = "contact@zotaris.com";

const socialLinks = [
   {
      label: "Email",
      href: `mailto:${CONTACT_EMAIL}`,
      icon: emailIcon,
   },
   {
      label: "Instagram",
      href: "https://www.instagram.com/",
      icon: instagramIcon,
   },
   {
      label: "LinkedIn",
      href: "https://www.linkedin.com/",
      icon: linkedinIcon,
   },
   {
      label: "Facebook",
      href: "https://www.facebook.com/",
      icon: facebookIcon,
   },
];

function FooterLink({ item }) {
   const [label, href] = item;

   if (href.startsWith("http") || href.startsWith("mailto:")) {
      return (
         <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
            {label}
         </a>
      );
   }

   return <Link to={href}>{label}</Link>;
}

function Footer() {
   const { t } = useTranslation();
   const columns = [
      {
         title: t("footer.services"),
         links: [
            [t("footer.automation"), "/services/automation"],
            [t("footer.websites"), "/services/websites-digital-presence"],
            [t("footer.platforms"), "/services/custom-web-platforms"],
         ],
      },
      {
         title: t("footer.company"),
         links: [
            [t("footer.projects"), "/projects"],
            [t("footer.consulting"), "/services/digital-strategy-consulting"],
            [t("footer.courses"), "/services/ai-automation-courses"],
         ],
      },
      {
         title: t("footer.contact"),
         links: [
            [t("footer.contact"), `mailto:${CONTACT_EMAIL}`],
            [t("footer.address"), "https://maps.google.com/?q=31%20August%201989%20St%2078"],
         ],
      },
   ];

   return (
      <footer className="footer">
         <div className="container">
            <div className="footer__body">
               <div className="footer__layout">
                  <div className="footer__brand">
                     <Link to="/" className="footer__logo" aria-label="Zotaris">
                        <ReactSVG src={logo} />
                     </Link>
                     <p>{t("footer.intro")}</p>
                     <div className="footer__socials" aria-label={t("common.socialLinks")}>
                        {socialLinks.map((social) => (
                           <a
                              className="glass-effect"
                              href={social.href}
                              target={social.href.startsWith("http") ? "_blank" : undefined}
                              rel={social.href.startsWith("http") ? "noreferrer" : undefined}
                              aria-label={social.label}
                              key={social.label}
                           >
                              <ReactSVG src={social.icon} />
                           </a>
                        ))}
                     </div>
                  </div>

                  <div className="footer__columns">
                     {columns.map((column, index) => (
                        <nav
                           className={`footer__column footer__column--${index + 1} glass-container-effect`}
                           aria-label={column.title}
                           key={column.title}
                        >
                           <MatrixBackdrop
                              className="footer__matrix"
                              cols={index === 2 ? 42 : 34}
                              rows={index === 1 ? 14 : 12}
                              seed={81 + index * 29}
                              pattern={index === 0 ? "linear" : index === 1 ? "center-soft" : "corner"}
                              colorA={index === 1 ? "111, 111, 255" : "218, 255, 86"}
                              colorB="255, 255, 255"
                           />
                           <h3>{column.title}</h3>
                           <div className="footer__links">
                              {column.links.map((item) => (
                                 <FooterLink item={item} key={item[0]} />
                              ))}
                           </div>
                        </nav>
                     ))}
                  </div>
               </div>

               <div className="footer__bottom">
                  <p>{t("footer.copyright")}</p>
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
               </div>
            </div>
         </div>
      </footer>
   );
}

export default Footer;
